import React, { Suspense, lazy, useMemo, useState, useEffect } from 'react';
import { useNavigation } from '../contexts/NavigationContext';
import { useAuth } from '../contexts/AuthContext';
import { COURSE_UNITS_DATA } from '../data/unitsData';
import Whiteboard from '../components/Whiteboard'; 
import { 
    FaSpinner, FaExclamationTriangle, 
    FaArrowLeft, FaArrowRight, 
    FaStepForward, 
    FaListAlt, FaGlobeAmericas,
    FaChalkboardTeacher 
} from 'react-icons/fa';

// MAPPING: Converts raw filenames to nice display titles
const LESSON_TITLES = {
    "AdventureKidsLesson1.js": "Morning Activities & Holidays",
    "AdventureKidsLesson2.js": "Space Adventures",
    "AdventureKidsLesson3.js": "Adventure Conversations",
    "BeginnerKidsLesson1.js": "Hello World!",
    "BeginnerKidsLesson2.js": "Art & Numbers",
    "BeginnerKidsLesson3.js": "The Camping Trip",
    "BeginnerKidsLesson4.js": "Home & Family Stories",
    "BeginnerKidsLesson5.js": "A Trip to the Mall",
    "BeginnerKidsLesson6.js": "Let's Eat! Roleplay",
    "BeginnerKidsLesson7.js": "Weather & Seasons",
    "BeginnerKidsLesson8.js": "My Day",
    "BeginnerKidsLesson9.js": "Clothes & Fashion",
    "BeginnerKidsLesson10.js": "Home & Family",
    "BeginnerKidsLesson11.js": "Weather Reporter",
    "BeginnerKidsLesson12.js": "Forest Camping",
    "BeginnerKidsLesson13.js": "Travel & Transport",
    "BeginnerKidsLesson14.js": "The Toy Shop",
    "BeginnerKidsLesson15.js": "Level 1 Capstone"
};

const LessonPage = () => {
    const { currentPage, currentPageData, navigateTo } = useNavigation();
    const { user } = useAuth(); 

    const { courseId, lessonFileName, timeLimit } = currentPageData || {};

    const [accessDenied, setAccessDenied] = useState(false);
    const [showWhiteboard, setShowWhiteboard] = useState(false); 
    const [navState, setNavState] = useState({
        prev: null, next: null, courseStart: null, nextCourse: null,
        currentTitle: '', isFirstLesson: false, isLastLesson: false
    });
    const [courseLessonList, setCourseLessonList] = useState([]);

    // --- SECURITY GATE FOR GUESTS ---
    useEffect(() => {
        if (!user && courseId && lessonFileName) {
            const currentCourse = COURSE_UNITS_DATA[courseId];
            if (currentCourse && currentCourse.units.length > 0) {
                // The filename of the very first lesson in the entire course
                const firstLessonAllowed = currentCourse.units[0].lessons[0];
                
                // If the guest is trying to access anything other than the first file
                if (lessonFileName !== firstLessonAllowed) {
                    alert("Guests are only permitted to view the first lesson. Please log in for full access.");
                    navigateTo('login');
                    setAccessDenied(true);
                }
            }
        }
    }, [user, courseId, lessonFileName, navigateTo]);

    // --- CALCULATE NAVIGATION ---
    useEffect(() => {
        if (!courseId || !lessonFileName || accessDenied || !COURSE_UNITS_DATA[courseId]) return;

        const courseKeys = Object.keys(COURSE_UNITS_DATA);
        const currentCourseIndex = courseKeys.indexOf(courseId);
        const currentCourse = COURSE_UNITS_DATA[courseId];

        const currentCourseLessons =[];
        currentCourse.units.forEach(u => {
            u.lessons.forEach((l, index) => {
                const friendlyName = LESSON_TITLES[l] || l.replace('.js', '').replace(/([A-Z])/g, ' $1').trim();
                currentCourseLessons.push({
                    courseId: courseId,
                    courseTitle: currentCourse.title,
                    unitId: u.unitId,
                    unitTitle: u.title,
                    fileName: l,
                    timeLimit: u.timeLimit,
                    friendlyName: friendlyName,
                    label: `L${index + 1}: ${friendlyName}` 
                });
            });
        });
        setCourseLessonList(currentCourseLessons);

        const currentIndex = currentCourseLessons.findIndex(l => l.fileName === lessonFileName);
        
        let prev = null;
        let next = null;
        let courseStart = null;
        let nextCourse = null;

        if (currentIndex !== -1) {
            courseStart = currentCourseLessons[0];
            if (currentIndex > 0) prev = currentCourseLessons[currentIndex - 1];
            if (currentIndex < currentCourseLessons.length - 1) next = currentCourseLessons[currentIndex + 1];

            if (currentCourseIndex < courseKeys.length - 1) {
                const nextCourseId = courseKeys[currentCourseIndex + 1];
                const nextCourseData = COURSE_UNITS_DATA[nextCourseId];
                if (nextCourseData.units.length > 0 && nextCourseData.units[0].lessons.length > 0) {
                    nextCourse = {
                        courseId: nextCourseId,
                        courseTitle: nextCourseData.title,
                        unitId: nextCourseData.units[0].unitId,
                        unitTitle: nextCourseData.units[0].title,
                        fileName: nextCourseData.units[0].lessons[0],
                        timeLimit: nextCourseData.units[0].timeLimit
                    };
                }
            }
        }

        setNavState({
            prev, next, courseStart, nextCourse,
            currentTitle: currentCourseLessons[currentIndex]?.friendlyName || 'Lesson',
            isFirstLesson: currentIndex === 0,
            isLastLesson: currentIndex === currentCourseLessons.length - 1
        });

    }, [courseId, lessonFileName, accessDenied]);

    const goTo = (target) => {
        if (!target) return;
        
        // --- LOCK NAVIGATION FOR GUESTS ---
        if (!user) {
            alert("Please Login to continue to the next unit.");
            navigateTo('login');
            return;
        }

        const path = target.fileName.replace('.js', '');
        navigateTo(`lessons/${path}`, {
            courseId: target.courseId,
            courseTitle: target.courseTitle,
            unitId: target.unitId,
            unitTitle: target.unitTitle,
            lessonFileName: target.fileName,
            timeLimit: target.timeLimit
        });
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    const handleJumpToLesson = (e) => {
        const selectedFileName = e.target.value;
        const target = courseLessonList.find(l => l.fileName === selectedFileName);
        if (target) goTo(target);
    };

    const LessonComponent = useMemo(() => {
        const cleanName = currentPage.replace('lessons/', '').replace('.js', '');
        if (!cleanName) return () => <div className="text-center p-10 text-red-500">Error: No lesson path.</div>;

        return lazy(() => 
            import(`./lessons/${cleanName}.js`)
            .then(module => {
                let Component = module.default;
                if (!Component && module && Object.keys(module).length > 0) {
                    const namedExport = Object.values(module).find(exp => typeof exp === 'function');
                    if (namedExport) Component = namedExport;
                }
                return { default: Component };
            })
            .catch(err => {
                return { default: () => (
                    <div className="text-center p-10 bg-red-50 rounded-xl border border-red-200 mt-10 shadow-sm">
                        <FaExclamationTriangle className="text-red-500 text-5xl mx-auto mb-4" />
                        <h2 className="font-bold text-red-700 text-2xl">Content Unavailable</h2>
                        <p className="text-gray-600 font-medium mt-2">Could not find the lesson file.</p>
                    </div>
                ) };
            })
        );
    }, [currentPage]);

    if (accessDenied) return null;

    const canUseWhiteboard = user && (user.role === 'teacher' || user.role === 'master');

    return (
        <div className="min-h-screen bg-gray-50 pb-32 font-sans relative">
            <div className="bg-white shadow-md sticky top-0 z-30 px-4 py-3 border-b border-gray-200 flex flex-col md:flex-row justify-between items-center gap-3">
                <div className="flex items-center gap-4 w-full md:w-auto">
                    <button 
                        onClick={() => navigateTo('units', { courseId, title: currentPageData?.courseTitle })} 
                        className="flex items-center gap-2 bg-gray-100 hover:bg-blue-50 text-gray-700 hover:text-blue-600 px-4 py-2 rounded-lg transition-colors font-bold text-sm border border-gray-300"
                    >
                        <FaArrowLeft /> Units
                    </button>
                    <div className="hidden md:block h-8 w-px bg-gray-300"></div>
                    <h1 className="text-sm md:text-base font-black text-gray-800 truncate">
                        <span className="text-gray-500 font-normal">{currentPageData?.courseTitle} / </span>
                        {navState.currentTitle}
                    </h1>
                </div>

                <div className="flex items-center gap-2 w-full md:w-auto">
                    <select 
                        value={lessonFileName || ''} 
                        onChange={handleJumpToLesson}
                        className="bg-gray-50 border border-gray-300 text-gray-700 text-sm font-semibold rounded-lg p-2 focus:ring-2 focus:ring-blue-500 outline-none cursor-pointer flex-grow md:w-64"
                    >
                        {courseLessonList.map((lesson, idx) => (
                            <option key={idx} value={lesson.fileName}>
                                {lesson.label}
                            </option>
                        ))}
                    </select>
                </div>
            </div>

            {canUseWhiteboard && (
                <>
                    <button 
                        onClick={() => setShowWhiteboard(true)}
                        className="fixed bottom-32 right-4 bg-purple-600 text-white p-4 rounded-full shadow-2xl hover:bg-purple-700 z-50 flex items-center gap-2 font-bold transition-transform active:scale-95"
                    >
                        <FaChalkboardTeacher size={24} />
                    </button>
                    {showWhiteboard && <Whiteboard onClose={() => setShowWhiteboard(false)} />}
                </>
            )}
            
            <div className="p-4 md:p-8 max-w-6xl mx-auto">
                <Suspense fallback={<div className="text-center p-20"><FaSpinner className="animate-spin text-4xl mx-auto text-indigo-500"/></div>}>
                    <LessonComponent timeLimit={timeLimit} />
                </Suspense>
            </div>

            <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 p-2 z-50 shadow-[0_-4px_6px_-1px_rgba(0,0,0,0.1)]">
                <div className="max-w-5xl mx-auto grid grid-cols-4 md:grid-cols-6 gap-2 text-xs font-bold">
                    <button onClick={() => goTo(navState.prev)} disabled={!navState.prev} className={`py-3 rounded-lg flex flex-col items-center justify-center gap-1 transition-colors ${!navState.prev ? 'text-gray-300' : 'bg-gray-100 hover:bg-blue-50 text-gray-700'}`}>
                        <FaArrowLeft /> <span>Prev</span>
                    </button>

                    <button onClick={() => goTo(navState.next)} disabled={!navState.next} className={`col-span-2 md:col-span-1 py-3 rounded-lg flex flex-col items-center justify-center gap-1 transition-colors ${!navState.next ? 'text-gray-300' : 'bg-blue-600 text-white hover:bg-blue-700 shadow-md'}`}>
                        <span>Next Lesson</span> <FaArrowRight />
                    </button>

                    <button onClick={() => navigateTo('units', { courseId, title: currentPageData?.courseTitle })} className="py-3 rounded-lg flex flex-col items-center justify-center gap-1 text-gray-600 hover:bg-gray-100 transition-colors">
                        <FaListAlt /> <span>Menu</span>
                    </button>

                    <button onClick={() => navigateTo('eslCourses')} className="py-3 rounded-lg flex flex-col items-center justify-center gap-1 text-gray-600 hover:bg-gray-100 transition-colors">
                        <FaGlobeAmericas /> <span>Lessons</span>
                    </button>
                    
                    <button onClick={() => goTo(navState.nextCourse)} disabled={!navState.nextCourse} className={`hidden md:flex py-3 rounded-lg flex flex-col items-center justify-center gap-1 transition-colors ${!navState.nextCourse ? 'text-gray-300' : 'bg-green-50 text-green-700 hover:bg-green-100'}`}>
                        <span>Next Level</span> <FaStepForward />
                    </button>
                </div>
            </div>
        </div>
    );
};

export default LessonPage;